import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';

const defaultFormatHeartbeat = (value) => {
  if (!value) return 'No heartbeat recorded';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  return parsed.toLocaleString();
};

const defaultRenderSecondary = (camera, heartbeatText) => (
  <Stack spacing={0.5}>
    <Typography variant="body2" color="text.secondary">
      {camera.location || 'Location not specified'}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      Last heartbeat: {heartbeatText}
    </Typography>
  </Stack>
);

const CameraList = ({
  title,
  description,
  cameras,
  loading,
  error,
  onRefresh,
  refreshLabel = 'Refresh',
  formatHeartbeat = defaultFormatHeartbeat,
  onPreview,
  previewButtonLabel = 'View live',
  previewLoading = false,
  pendingPreviewId = null,
  emptyMessage = 'No cameras available.',
  headerIcon = <VideocamIcon />,
  renderSecondary = defaultRenderSecondary,
}) => (
  <Card sx={{ borderRadius: '12px' }}>
    <CardContent>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              bgcolor: 'rgba(25, 118, 210, 0.12)',
              color: 'primary.main',
            }}
          >
            {headerIcon}
          </Box>
          <Box>
            <Typography variant="h6">{title}</Typography>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>
        </Stack>
        {onRefresh && (
          <Button variant="outlined" onClick={onRefresh} disabled={loading}>
            {refreshLabel}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : cameras.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      ) : (
        <List disablePadding>
          {cameras.map((camera, index) => {
            const heartbeatText = formatHeartbeat(camera.last_health_check);
            const isOnline = camera.is_active !== false && !!camera.last_health_check;
            const statusColor = isOnline ? 'success.main' : 'text.secondary';
            const statusLabel = isOnline ? 'Online' : 'Offline';
            const isPending = previewLoading && pendingPreviewId === camera.id;

            return (
              <React.Fragment key={camera.id ?? index}>
                <ListItem
                  alignItems="flex-start"
                  sx={{ px: 0, py: 1.5 }}
                  secondaryAction={
                    onPreview && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => onPreview(camera)}
                        disabled={isPending}
                      >
                        {isPending ? 'Opening...' : previewButtonLabel}
                      </Button>
                    )
                  }
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <Typography fontWeight={600}>{camera.name || `Camera ${camera.id}`}</Typography>
                        <Typography variant="caption" sx={{ color: statusColor }}>
                          {statusLabel}
                        </Typography>
                      </Stack>
                    }
                    secondary={renderSecondary(camera, heartbeatText)}
                  />
                </ListItem>
                {index < cameras.length - 1 && <Divider component="li" />}
              </React.Fragment>
            );
          })}
        </List>
      )}
    </CardContent>
  </Card>
);

CameraList.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  cameras: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRefresh: PropTypes.func,
  refreshLabel: PropTypes.string,
  formatHeartbeat: PropTypes.func,
  onPreview: PropTypes.func,
  previewButtonLabel: PropTypes.string,
  previewLoading: PropTypes.bool,
  pendingPreviewId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  emptyMessage: PropTypes.string,
  headerIcon: PropTypes.element,
  renderSecondary: PropTypes.func,
};

CameraList.defaultProps = {
  description: '',
  loading: false,
  error: '',
  onRefresh: undefined,
  refreshLabel: 'Refresh',
  formatHeartbeat: defaultFormatHeartbeat,
  onPreview: undefined,
  previewButtonLabel: 'View live',
  previewLoading: false,
  pendingPreviewId: null,
  emptyMessage: 'No cameras available.',
  headerIcon: <VideocamIcon />,
  renderSecondary: defaultRenderSecondary,
};

export default CameraList;
